import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AppState, ScanResult } from './types';
import { supabase, createTables, saveScan } from './lib/supabase';

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      language: 'ru',
      history: [],
      pendingScans: [],
      setLanguage: (lang) => set({ language: lang }),
      addPendingScan: (scan) => {
        set(state => ({
          pendingScans: [scan, ...state.pendingScans]
        }));
      },
      removePendingScan: (timestamp) => {
        set(state => ({
          pendingScans: state.pendingScans.filter(scan => scan.timestamp !== timestamp)
        }));
      },
      confirmPendingScan: async (timestamp) => {
        const state = get();
        const scan = state.pendingScans.find(s => s.timestamp === timestamp);
        
        if (!scan) {
          return { success: false, message: 'Скан не найден' };
        }

        try {
          // Try to save to database first - it will check for duplicates
          await saveScan({ 
            tracking_numbers: scan.tracking_numbers.trim().toLowerCase(),
            format: scan.format
          });

          // Add to confirmed history
          state.addScan(scan);
          
          // Remove from pending
          state.removePendingScan(timestamp);

          return { success: true, message: 'Трек-номер успешно сохранен' };
        } catch (error) {
          console.error('Error confirming scan:', error);
          return { 
            success: false, 
            message: error instanceof Error ? error.message : 'Ошибка при сохранении'
          };
        }
      },
      removeScan: (timestamp) => set(state => ({
        history: state.history.filter(scan => scan.timestamp !== timestamp)
      })),
      addScan: (scan) => {
        try {
          // Basic validation
          const trackingNumbers = scan?.tracking_numbers?.trim() || '';
          
          if (!trackingNumbers) {
            console.error('Invalid tracking number');
            return { success: false, message: 'Invalid tracking number' };
          }

          // Update local history with non-duplicate scan
          set(state => ({
            history: [scan, ...state.history]
          }));

          return { success: true, message: 'Scan saved successfully' };
        } catch (error) {
          console.error('Error in addScan:', error);
          return { success: false, message: `Unexpected error occurred: ${error instanceof Error ? error.message : String(error)}` };
        }
      },
      deleteScan: async (timestamp) => {
        try {
          const isoTimestamp = new Date(timestamp).toISOString();
          console.log('Attempting to delete scan with timestamp:', isoTimestamp);

          // Delete from both tables
          const { data, error } = await supabase
            .from('scans')
            .delete()
            .eq('timestamp', isoTimestamp);

          // Also delete from scans_2
          await supabase
            .from('scans_2')
            .delete()
            .eq('timestamp', isoTimestamp);

          if (error) {
            console.error('Error deleting scan:', error);
            return { success: false, message: `Error deleting scan: ${error.message}` };
          }

          // Update local state
          set(state => ({
            history: state.history.filter(item => item.timestamp !== timestamp)
          }));
          
          // Sync with server to ensure consistency
          await get().syncHistory();
          
          return { success: true, message: 'Scan deleted successfully' };
        } catch (error) {
          console.error('Error in deleteScan:', error);
          return { success: false, message: `Error deleting scan: ${error instanceof Error ? error.message : String(error)}` };
        }
      },
      clearHistory: async () => {
        try {
          // Delete from scans table
          const { error: scansError } = await supabase
            .from('scans_2')
            .delete()
            .eq('project_id', 'scanner');

          if (scansError) {
            console.error('Error clearing history from scans_2 table:', scansError);
          }
          
          // Delete from scans_2 table
          const { error: scans2Error } = await supabase
            .from('scans')
            .delete()
            .eq('project_id', 'scanner');

          if (scans2Error) {
            console.error('Error clearing history from scans table:', scans2Error);
          }

          set({ history: [] });
          return { success: true, message: 'History cleared successfully' };
        } catch (error) {
          console.error('Error in clearHistory:', error);
          return { success: false, message: `Error clearing history: ${error instanceof Error ? error.message : String(error)}` };
        }
      },
      syncHistory: async () => {
        try {
          console.log('Syncing history with database...');
          
          // First try to create tables if they don't exist
          const { success: tablesCreated, error: createError } = await createTables();
          
          if (!tablesCreated) {
            console.error('Error creating tables:', createError);
            set({ history: [] });
            return;
          }
          
          let { data, error } = await supabase
            .from('scans_2')
            .select('*')
            .order('timestamp', { ascending: false });

          if (error) {
            // Try scans table as fallback
            const result = await supabase
              .from('scans')
              .select('*')
              .order('timestamp', { ascending: false });
              
            if (result.error) {
              console.error('Error syncing from both tables:', error);
              set({ history: [] });
              return;
            }
            
            data = result.data;
          }

          console.log('Retrieved data from database:', data?.length || 0, 'records');
          const formattedData: ScanResult[] = data ? data.map(scan => {
            return {
              tracking_numbers: typeof scan.tracking_numbers === 'string' ? scan.tracking_numbers.trim() : String(scan.tracking_numbers).trim(),
              timestamp: new Date(scan.timestamp).getTime(),
              format: scan.format
            };
          }) : [];

          set({ history: formattedData });
        } catch (error) {
          console.error('Error in syncHistory:', error, error instanceof Error ? error.stack : '');
          // Set empty history on error to prevent UI issues
          set({ history: [] });
        }
      }
    }),
    {
      name: 'barcode-scanner-storage',
      partialize: (state) => ({
        language: state.language
      })
    }
  )
);

export const get = useStore.getState;